import { useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
} from '@artstorefronts/ui';
import { EllipsisIcon, PencilIcon, TrashIcon, XIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useMutation } from '@apollo/client';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { UPDATE_CHAT_TITLE, DELETE_CHAT, UPDATE_CHAT_SHARED } from '~/graphql/mutations';
import { ConfirmDeleteDialog } from './ConfirmDeleteDialog';
import { ChatRenameDialog } from './ChatRenameDialog';
import { getChatNavigationPath } from '~/utils/chatNavigation';
import { chatHistory } from '~/utils/chatHistory';

interface ChatContextMenuProps {
  chatId: string;
  chatTitle: string;
  chatType?: string;
  chatTargetId?: string;
  chatShared?: boolean;
  className?: string;
  onOpenChange?: (open: boolean) => void;
}

export function ChatContextMenu({
  chatId,
  chatTitle,
  chatType,
  chatTargetId,
  chatShared,
  className,
  onOpenChange,
}: ChatContextMenuProps) {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('chat');
  const { t: tc } = useTranslation('common');

  const [updateChatTitle, { loading: updating }] = useMutation(UPDATE_CHAT_TITLE, {
    // By operation name: refreshes every mounted chat list whatever its scope variables.
    refetchQueries: ['MyChats'],
    onCompleted: () => {
      toast.success(t('context-menu.title-updated'));
      setIsRenameDialogOpen(false);
    },
    onError: (error) => {
      toast.error(t('context-menu.title-update-failed'));
      console.error('Update chat title error:', error);
    },
  });

  const [deleteChat, { loading: deleting }] = useMutation(DELETE_CHAT, {
    refetchQueries: ['MyChats'],
    onCompleted: () => {
      toast.success(t('context-menu.deleted'));
      setIsDeleteDialogOpen(false);
    },
    onError: (error) => {
      toast.error(t('context-menu.delete-failed'));
      console.error('Delete chat error:', error);
    },
  });

  const [updateChatShared, { loading: updatingShared }] = useMutation(UPDATE_CHAT_SHARED, {
    refetchQueries: ['MyChats'],
    onCompleted: () => {
      toast.success(t('context-menu.unshared'));
    },
    onError: (error) => {
      toast.error(t('context-menu.unshare-failed'));
      console.error('Un-share chat error:', error);
    },
  });

  const handleRenameClick = (e: Event) => {
    e.preventDefault();
    setIsRenameDialogOpen(true);
  };

  const handleDeleteClick = (e: Event) => {
    e.preventDefault();
    setIsDeleteDialogOpen(true);
  };

  const handleUnshareClick = async (e: Event) => {
    e.preventDefault();
    await updateChatShared({
      variables: {
        input: {
          id: chatId,
          shared: false,
        },
      },
    });
  };

  const handleRenameConfirm = async (newTitle: string) => {
    await updateChatTitle({
      variables: {
        input: {
          id: chatId,
          title: newTitle,
        },
      },
    });
  };

  const handleDeleteConfirm = async () => {
    const result = await deleteChat({
      variables: {
        input: {
          id: chatId,
        },
      },
    });

    // If we're currently viewing the deleted chat, navigate to /chat
    if (result.data?.deleteChat) {
      const chatPath = getChatNavigationPath({ id: chatId, type: chatType, targetId: chatTargetId });
      if (location.pathname + location.search === chatPath) {
        chatHistory.clearLastConversation();
        navigate('/chat', { replace: true });
      }
    }
  };

  return (
    <>
      <DropdownMenu modal={false} onOpenChange={onOpenChange}>
        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
          <div
            className={`h-6 w-6 p-1 rounded-lg flex items-center justify-center cursor-pointer ${className}`}
            aria-label={t('context-menu.chat-options')}
            onClick={(e) => e.stopPropagation()}
          >
            <EllipsisIcon className="w-4 h-4 text-black" />
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuPortal>
          <DropdownMenuContent align="end" data-no-drag-select="true" onClick={(e) => e.stopPropagation()}>
            <DropdownMenuItem className="cursor-pointer" onSelect={handleRenameClick}>
              <PencilIcon className="w-4 h-4 mr-2" /> <span>{t('context-menu.rename')}</span>
            </DropdownMenuItem>
            {chatShared && (
              <DropdownMenuItem className="cursor-pointer" onSelect={handleUnshareClick} disabled={updatingShared}>
                <XIcon className="w-4 h-4 mr-2" />
                <span>{updatingShared ? t('context-menu.un-sharing') : t('context-menu.un-share')}</span>
              </DropdownMenuItem>
            )}
            <DropdownMenuItem className="cursor-pointer text-red-500 hover:text-red-500" onSelect={handleDeleteClick}>
              <TrashIcon className="w-4 h-4 mr-2" /> <span>{tc('delete')}</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenuPortal>
      </DropdownMenu>

      <ChatRenameDialog
        open={isRenameDialogOpen}
        onOpenChange={setIsRenameDialogOpen}
        currentTitle={chatTitle}
        onConfirm={handleRenameConfirm}
        isLoading={updating}
      />

      <ConfirmDeleteDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onConfirm={handleDeleteConfirm}
        isLoading={deleting}
        itemName=""
        description={t('context-menu.delete-confirm', { title: chatTitle })}
      />
    </>
  );
}
